import ErrorComponent from "@/components/atoms/ErrorComponent";
import Flex from "@/components/atoms/Flex";
import IconButton from "@/components/atoms/IconButton";
import Loader from "@/components/atoms/Loader";
import { SelectPaginateAsync } from "@/components/atoms/Select";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { EMAIL_REGEX } from "@/constants/regexs";
import {
  INVALID_EMAIL2_MESSAGE,
  INVALID_FORM_MESSAGE,
  INVALID_MAXIMUM_CARACTERE_MESSAGE,
  INVALID_MINIMUM_CARACTERE_MESSAGE,
  INVALID_NAME_MESSAGE,
  INVALID_PHONE2_MESSAGE,
} from "@/constants/validatorMessages";

import getDifferenceBetweenTwoObjects from "@/helpers/FormHelpers";

import useLoadOptions from "@/hooks/useLoadOptions";
import { CardEditProps } from "@/interfaces/Card";
import Company, { CompanyForm } from "@/interfaces/Company";
import { IPaginateResponse } from "@/interfaces/Fetch";
import {
  useGetCompanyQuery,
  useUpdateCompanyMutation,
} from "@/services/companies";

import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import { DateTime } from "luxon";
import { ElementRef, FC, useCallback, useEffect, useRef } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ToastContentProps, toast } from "react-toastify";
import CardHeader from "../../card-headers/CardHeader";
import StoreCompanyNoteModal from "../../modals/company/StoreCompanyNoteModal";

const CompanyEditCard: FC<CardEditProps<Company>> = ({ id }) => {
  const navigation = useNavigate();
  const {
    handleSubmit,
    control,
    setValue,
    register,
    watch,
    reset,
    formState: { errors },
  } = useForm<Partial<CompanyForm>>();
  const [updateCompanyAsync] = useUpdateCompanyMutation();
  const StoreCompanyNoteRef =
    useRef<ElementRef<typeof StoreCompanyNoteModal>>(null);

  const onTriggerNoteModal = () =>
    StoreCompanyNoteRef.current?.showOrHiddenModal(id);

  const noteButton = (
    <IconButton
      type="button"
      variant="primary"
      size="sm"
      icon={faPlus}
      iconAlign="left"
      onClick={onTriggerNoteModal}
    >
      Ajouter une note
    </IconButton>
  );

  const onCallback = useCallback(
    (name: string, response: IPaginateResponse<any>) => {
      if (name === "loadCompanyStatusesOptions" && !watch("status")) {
        setValue("status", response.data[0]);
      }
    },
    [],
  );

  const { loadCompanyStatusesOptions, loadActivitySectors } = useLoadOptions({
    onCallback,
  });

  const defaultAdditional = useRef<{ pageIndex: number }>({
    pageIndex: 0,
  }).current;

  const { data, isLoading, isError, error } = useGetCompanyQuery(id);

  useEffect(() => {
    if (data) {
      reset({
        ...data,
        notes: [],
        owner: {
          firstname: data.owner?.firstname,
          lastname: data.owner?.lastname,
          phone: data.owner?.phone,
        },
      });
    }
  }, [data]);

  const onSubmit: SubmitHandler<Partial<CompanyForm>> = async (newData) => {
    const { status, owner, phone, fixPhone, ...result } =
      getDifferenceBetweenTwoObjects<Partial<CompanyForm>>(data, newData);

    const ownerFields = {
      ...(owner?.firstname || data?.owner?.firstname
        ? { firstname: owner?.firstname || data?.owner?.firstname }
        : {}),
      ...(owner?.lastname || data?.owner?.lastname
        ? { lastname: owner?.lastname || data?.owner?.lastname }
        : {}),
      ...(owner?.phone && {
        phone: parsePhoneNumber(owner.phone, "FR")!.number,
      }),
    };
    const ownerData =
      Object.keys(ownerFields).length > 0 ? ownerFields : undefined;

    toast.promise(
      updateCompanyAsync([
        id,
        {
          ...result,
          statusId: status?.id,
          phone: phone && parsePhoneNumber(phone, "FR")!.number,
          fixPhone: fixPhone && parsePhoneNumber(fixPhone, "FR")!.number,
          ...(ownerData && { owner: ownerData }),
          activitySectorId: newData.activitySector?.id || null,
        },
      ]).unwrap(),
      {
        pending: "Modification de l'entreprise en cours ...",
        success: {
          render() {
            navigation(-1);
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                L'entreprise à été modifiée 🤩
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              return "Les champs que vous avez remplis semblent être incorrects.";
            }

            return "Une erreur est survenue";
          },
        },
      },
    );
  };

  const onError: SubmitErrorHandler<Partial<CompanyForm>> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  if (isLoading) {
    return <Loader absolute />;
  } else if (isError) {
    return <ErrorComponent error={error} absolute returnButton />;
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit, onError)}>
      <StoreCompanyNoteModal ref={StoreCompanyNoteRef} />
      <Card>
        <CardHeader title="Modification de l'entreprise" endEl={noteButton} />
        <Card.Body className="bg-light">
          <Controller
            control={control}
            name="status"
            rules={{
              required: "Veuillez sélectionner au moins un statut",
            }}
            render={({ field: status }) => (
              <Row className="gx-3">
                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Statut <span className="text-danger">*</span> :
                    </Form.Label>
                    <SelectPaginateAsync
                      loadOptions={loadCompanyStatusesOptions}
                      value={status.value}
                      defaultOptions
                      additional={defaultAdditional}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id.toString()}
                      onChange={status.onChange}
                    />

                    <Form.Text className="text-muted">
                      Le choix du status de l&apos;entreprise est important car
                      il permet de définir les champs obligatoires ou non.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Nom de l'entreprise <span className="text-danger">*</span>{" "}
                      :
                    </Form.Label>
                    <Form.Control
                      {...register("name", {
                        required: {
                          value: true,
                          message: INVALID_NAME_MESSAGE,
                        },
                        minLength: {
                          value: 2,
                          message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                        },
                        maxLength: {
                          value: 255,
                          message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                        },
                      })}
                      placeholder="ex : Crédit Agricole"
                      type="text"
                      isInvalid={!!errors.name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.name?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Siret :</Form.Label>
                    <Form.Control
                      placeholder="ex : 552 178 639 00132"
                      type="text"
                      {...register("siret", {
                        validate: {
                          isLength15: (value) =>
                            !value ||
                            value.length === 14 ||
                            "Le numéro de siret doit comporter 14 chiffres",
                        },
                      })}
                      isInvalid={!!errors.siret}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.siret?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email :</Form.Label>
                    <Form.Control
                      placeholder="ex : credit@agricole.com"
                      type="text"
                      {...register("email", {
                        pattern: {
                          value: EMAIL_REGEX,
                          message: INVALID_EMAIL2_MESSAGE,
                        },
                      })}
                      isInvalid={!!errors.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Numéro de téléphone :</Form.Label>
                    <Form.Control
                      placeholder="ex : +33 6 34 83 55 32"
                      type="tel"
                      {...register("phone", {
                        validate: (value) => {
                          if (value && !isValidPhoneNumber(value, "FR")) {
                            return INVALID_PHONE2_MESSAGE;
                          }
                        },
                      })}
                      isInvalid={!!errors.phone}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.phone?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Numéro de téléphone fixe :</Form.Label>
                    <Form.Control
                      placeholder="ex : +33 1 34 83 55 32"
                      type="tel"
                      {...register("fixPhone", {
                        validate: (value) => {
                          if (value && !isValidPhoneNumber(value, "FR")) {
                            return INVALID_PHONE2_MESSAGE;
                          }
                        },
                      })}
                      isInvalid={!!errors.fixPhone}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.fixPhone?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Secteur d'activité</Form.Label>
                    <Controller
                      control={control}
                      name="activitySector"
                      render={({ field: { value, onChange } }) => (
                        <SelectPaginateAsync
                          loadOptions={loadActivitySectors}
                          getOptionLabel={(option) =>
                            `${option.opco.name} - ${option.name}`
                          }
                          getOptionValue={(option) => option.id.toString()}
                          isClearable
                          value={value}
                          onChange={onChange}
                          aria-invalid={!!errors.activitySector}
                        />
                      )}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.activitySector?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Adresse</Form.Label>
                    <Form.Control
                      placeholder="ex : 3 Rue de la Paix, 75002 Paris"
                      type="string"
                      {...register("address")}
                      isInvalid={!!errors.address}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.address?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Ville :</Form.Label>
                    <Form.Control
                      placeholder="ex : 3 Rue de la Paix, 75002 Paris"
                      type="string"
                      {...register("city")}
                      isInvalid={!!errors.city}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.city?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Code postale: </Form.Label>
                    <Form.Control
                      placeholder="ex : 3 Rue de la Paix, 75002 Paris"
                      type="string"
                      {...register("zipCode")}
                      isInvalid={!!errors.zipCode}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.zipCode?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Date et heure de rappel</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      {...register("reminderDatetime")}
                      isInvalid={!!errors.reminderDatetime}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.reminderDatetime?.message}
                    </Form.Control.Feedback>
                    <Form.Text>
                      La date et heure de rappel doit être supérieur à la date
                      d'aujourd'hui
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>N° adhérent OPCO</Form.Label>
                    <Form.Control
                      type="text"
                      {...register("opcoNumber")}
                      placeholder="ex : 23"
                      isInvalid={!!errors.opcoIdentifier}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.opcoIdentifier?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ID de l'OPCO</Form.Label>
                    <Form.Control
                      type="text"
                      {...register("opcoIdentifier")}
                      placeholder="ex : 23"
                      isInvalid={!!errors.opcoIdentifier}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.opcoIdentifier?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Mot de passe OPCO</Form.Label>
                    <Form.Control
                      type="text"
                      {...register("opcoPassword")}
                      isInvalid={!!errors.opcoPassword}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.opcoPassword?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Renouvellement :</Form.Label>
                    <Form.Select
                      {...register("renewal", {
                        setValueAs: (value) => value === "true",
                      })}
                      isInvalid={!!errors.renewal}
                    >
                      <option value="false">Non</option>
                      <option value="true">Oui</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.renewal?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col xs={12} className="mt-3">
                  <hr />
                  <h4 className="my-4">Interlocuteur :</h4>
                </Col>
                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Prenom :</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="ex : Marie"
                      {...register("interlocutorFirstname")}
                    />
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nom :</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="ex : Martin"
                      {...register("interlocutorLastname")}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} className="mt-3">
                  <hr />
                  <h4 className="my-4">Informations du gérant :</h4>
                </Col>
                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Prenom :</Form.Label>
                    <Form.Control
                      placeholder="ex : Remi"
                      type="text"
                      {...register("owner.firstname", {
                        validate: (value) => {
                          const lastname = watch("owner.lastname");
                          if (value && !lastname) {
                            return "Le nom du gérant est obligatoire si le prénom est renseigné";
                          }
                          if (!value && lastname) {
                            return "Le prénom du gérant est obligatoire si le nom est renseigné";
                          }
                        },
                      })}
                      isInvalid={!!errors.owner?.firstname}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.owner?.firstname?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nom :</Form.Label>
                    <Form.Control
                      placeholder="ex : Dupont"
                      type="text"
                      {...register("owner.lastname", {
                        validate: (value) => {
                          const firstname = watch("owner.firstname");
                          if (value && !firstname) {
                            return "Le prénom du gérant est obligatoire si le nom est renseigné";
                          }
                          if (!value && firstname) {
                            return "Le nom du gérant est obligatoire si le prénom est renseigné";
                          }
                        },
                      })}
                      isInvalid={!!errors.owner?.lastname}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.owner?.lastname?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Numéro de portable :</Form.Label>
                    <Form.Control
                      placeholder="ex : +33 6 34 83 55 32"
                      type="text"
                      {...register("owner.phone", {
                        validate: (value) => {
                          if (value && !isValidPhoneNumber(value, "FR")) {
                            return INVALID_PHONE2_MESSAGE;
                          }
                        },
                      })}
                      isInvalid={!!errors.owner?.phone}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.owner?.phone?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            )}
          />
        </Card.Body>
        <Card.Footer>
          <Flex justifyContent="between" alignItems="center">
            <Button type="submit" variant="success">
              Confirmer
            </Button>
            {noteButton}
          </Flex>
        </Card.Footer>
      </Card>
    </Form>
  );
};

export default CompanyEditCard;
